import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Lock, LockOpen } from 'lucide-react';
import { Button } from '@/shared/ui/buttons/Button';
import { ConfirmDialog } from '@/shared/ui/dialogs/ConfirmDialog';
import { useToast } from '@/shared/hooks/useToast';
import {
  useClientBlockParcelCreation,
  useClientUnblockParcelCreation,
} from '@/entities/client';
import { extractErrorMessage } from '@/shared/api/errors';

type Props = {
  clientId: string;
  blocked: boolean;
};

export const FeaturesClientsBlockParcelCreationButton = ({ clientId, blocked }: Props) => {
  const { t } = useTranslation();
  const toast = useToast();
  const [open, setOpen] = useState(false);

  const block = useClientBlockParcelCreation(clientId);
  const unblock = useClientUnblockParcelCreation(clientId);

  const mutation = blocked ? unblock : block;

  const handleConfirm = () => {
    mutation.mutate(undefined, {
      onSuccess: () => {
        toast.success(
          blocked ? t('clients.block.toastUnblocked') : t('clients.block.toastBlocked'),
        );
        setOpen(false);
      },
      onError: (err) => {
        toast.error(extractErrorMessage(err));
      },
    });
  };

  return (
    <>
      <Button
        variant={blocked ? 'primary' : 'danger'}
        leftIcon={blocked ? <LockOpen className="h-4 w-4" /> : <Lock className="h-4 w-4" />}
        onClick={() => setOpen(true)}
      >
        {blocked ? t('clients.block.unblockButton') : t('clients.block.blockButton')}
      </Button>

      <ConfirmDialog
        open={open}
        title={blocked ? t('clients.block.unblockTitle') : t('clients.block.blockTitle')}
        description={
          blocked ? t('clients.block.unblockDescription') : t('clients.block.blockDescription')
        }
        confirmLabel={t('clients.block.confirm')}
        cancelLabel={t('clients.block.cancel')}
        confirmVariant={blocked ? 'primary' : 'danger'}
        loading={mutation.isPending}
        onConfirm={handleConfirm}
        onCancel={() => {
          if (!mutation.isPending) setOpen(false);
        }}
      />
    </>
  );
};
